import React, { FC } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@org/ui";
import { Separator } from "@org/ui";
import { Badge } from "@org/ui";
import { Block, ContentItem } from "@org/ui";

type ClassSummaryProps = {
  classData: Record<string, any>;
  blocks: Block[];
};

export const ClassSummary: FC<ClassSummaryProps> = ({ classData, blocks }) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Summary</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <ClassInfoSummary classData={classData} />
        {blocks.length > 0 && <BlocksSummary blocks={blocks} />}
      </CardContent>
    </Card>
  );
};

type ClassInfoSummaryProps = {
  classData: Record<string, any>;
};

const ClassInfoSummary: FC<ClassInfoSummaryProps> = ({ classData }) => {
  return (
    <Card className="bg-gray-50">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-medium">Class Information</CardTitle>
        <Separator />
      </CardHeader>
      <CardContent className="space-y-2">
        <p><span className="font-medium text-gray-700">Name:</span> {classData.name}</p>
        <p><span className="font-medium text-gray-700">Description:</span> {classData.description || "None"}</p>
      </CardContent>
    </Card>
  );
};

type BlocksSummaryProps = {
  blocks: Block[];
};

const BlocksSummary: FC<BlocksSummaryProps> = ({ blocks }) => {
  return (
    <Card className="bg-gray-50">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-medium">Content Blocks ({blocks.reduce((acc, block) => acc + (block.deleted ? 0 : 1), 0)})</CardTitle>
        <Separator />
      </CardHeader>
      <CardContent>
        <ol className="list-decimal pl-6 space-y-4">
          {blocks.map(block => (
            <BlockSummaryItem key={block.id} block={block} />
          ))}
        </ol>
      </CardContent>
    </Card>
  );
};

type BlockSummaryItemProps = {
  block: Block;
};

const BlockSummaryItem: FC<BlockSummaryItemProps> = ({ block }) => {
  return (
    <li className="text-gray-700 flex flex-col justify-start">
      <div className="flex flex-row justify-between">
        <div>
        <span className={"font-medium" + (block.deleted ? " line-through" : "")}>{block.name}</span>
        <Badge className={"ml-2" + (block.deleted ? " bg-red-500 text-white line-through" : "")}>Block</Badge>
        </div>
      </div>
      <div className="flex flex-row justify-between">
        {block.contents.length > 0 && <ContentsSummary contents={block.contents} />}
      </div>
    </li>
  );
};

type ContentsSummaryProps = {
  contents: ContentItem[];
};

const ContentsSummary: FC<ContentsSummaryProps> = ({ contents }) => {
  return (
    <div className="mt-2 ml-4">
      <p className="text-sm font-medium text-gray-700">Contents:</p>
      <ul className="list-disc pl-4 space-y-1 mt-1">
        {contents.map(content => (
          <li key={content.id} className="text-sm text-gray-600">
            <span className={"font-medium" + (content.deleted ? " line-through" : "")}>{content.name}</span>
            <Badge variant="outline" className={"ml-2 text-xs" + (content.deleted ? " bg-red-500 text-white line-through" : "")}>{content.type}</Badge>
          </li>
        ))}
      </ul>
    </div>
  );
};